import React, { useEffect, useRef, useState } from "react";

import { t } from "i18next";

import { Tune } from "@mui/icons-material";
import { Badge, Box, Button, IconButton } from "@mui/material";

import { theme } from "@/src/theme/theme";

interface SearchFilterButtonProps {
  isOpen: boolean;
  activeFilterCount: number;
  onToggle: () => void;
}

const SearchFilterButton = ({
  isOpen,
  activeFilterCount,
  onToggle
}: SearchFilterButtonProps) => {
  const [isSticky, setIsSticky] = useState(false);
  const firstFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When first filter is not visible (scrolled past), set sticky to true
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (firstFilterRef.current) {
      observer.observe(firstFilterRef.current);
    }

    return () => {
      if (firstFilterRef.current) {
        observer.unobserve(firstFilterRef.current);
      }
    };
  }, []);

  const textButtonComponent = (
    <Badge
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      overlap="circular"
      sx={{
        ".MuiBadge-badge": {
          top: ["8%", "10%"],
          right: ["5%", "8%"]
        },
        top: "auto",
        left: "auto",
        width: "100%",
        height: "auto"
      }}
      color="primary"
      variant="standard"
      badgeContent={
        !isOpen && activeFilterCount > 0 ? activeFilterCount : undefined
      }
    >
      <Button
        variant={isOpen ? "contained" : "outlined"}
        color="primary"
        startIcon={<Tune />}
        onClick={onToggle}
        sx={(theme) => ({
          fontSize: theme.typography.overline.fontSize,
          width: "100%"
        })}
      >
        {t("dealer.searchFilters.filterTitle")}
      </Button>
    </Badge>
  );

  const iconButtonComponent = (
    <Badge
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      overlap="circular"
      sx={{
        ".MuiBadge-badge": {
          top: "0.15rem",
          right: "0.5rem"
        },
        position: "fixed",
        bottom: "1rem",
        right: ["4.5rem", "4.5rem"],
        zIndex: 100
      }}
      color="primary"
      variant="standard"
      badgeContent={
        !isOpen && activeFilterCount > 0 ? activeFilterCount : undefined
      }
    >
      <IconButton
        color="primary"
        onClick={onToggle}
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: "1px solid",
          borderColor: theme.palette.semantic.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.misc.timelineBackground
          }
        }}
      >
        <Tune />
      </IconButton>
    </Badge>
  );

  // You can choose which one to render based on your needs
  const selectedComponent = isSticky
    ? iconButtonComponent
    : textButtonComponent; // or iconButtonComponent

  return (
    <Box
      sx={{
        position: "relative"
      }}
    >
      <div ref={firstFilterRef} />
      {selectedComponent}
    </Box>
  );
};

export default SearchFilterButton;
